<template>
  <BaseModal id="amazon-listing-editor" :value="true" @input="close">
    <template #header>
      <div class="flex flex-row items-center flex-wrap gap-x-3">
        <span class="flex items-center gap-1">
          <span class="font-semibold">{{ nkrCode }}</span>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-700 focus:outline-none"
            :title="$t('message.product_images.copy')"
            @click="copyHeaderCode(nkrCode)"
          >
            <i
              :class="[
                'mdi text-lg',
                copiedCode === nkrCode
                  ? 'mdi-check text-green-600'
                  : 'mdi-content-copy'
              ]"
            ></i>
          </button>
        </span>
        <span v-if="item" class="flex items-center gap-1">
          <span class="font-mono text-base text-gray-500">{{ item.sku }}</span>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-700 focus:outline-none"
            :title="$t('message.product_images.copy')"
            @click="copyHeaderCode(item.sku)"
          >
            <i
              :class="[
                'mdi text-lg',
                copiedCode === item.sku
                  ? 'mdi-check text-green-600'
                  : 'mdi-content-copy'
              ]"
            ></i>
          </button>
        </span>
        <span
          v-if="item && item.verifiedAt"
          class="px-2 py-0.5 rounded text-sm bg-green-100 text-green-700"
          :title="
            $t('message.marketplaces.editor_verified_by', {
              user: item.verifiedBy || '—',
              date: item.verifiedAt
            })
          "
        >
          <i class="mdi mdi-check-decagram"></i>
          {{ $t("message.marketplaces.editor_verified") }}
        </span>
      </div>
    </template>

    <div v-if="loading" class="py-12 flex justify-center">
      <BaseMiniSpinner />
    </div>

    <div v-else-if="!workspace" class="py-12 text-center text-gray-500">
      {{ $t("message.marketplaces.editor_load_error") }}
    </div>

    <div v-else class="p-2">
      <!-- Selector cuando el producto tiene más de un listing en Amazon -->
      <div v-if="workspace.items.length > 1" class="mb-4">
        <label class="text-sm text-gray-500 uppercase tracking-wide">
          {{ $t("message.marketplaces.editor_listing") }}
        </label>
        <select v-model="activeIndex" class="border rounded px-2 py-1 ml-2">
          <option v-for="(it, i) in workspace.items" :key="it.id" :value="i">
            {{ it.sku }} — {{ it.fulfillmentChannel }}
          </option>
        </select>
      </div>

      <!-- Sin ASIN no hay nada publicado: aca el trabajo no es corregir
           contenido sino crear el listing, asi que el panel ofrece esa salida
           en vez de dejar al usuario frente a un editor que no aplica. -->
      <div
        v-if="notInAmazon"
        class="mb-4 border border-yellow-200 bg-yellow-50 rounded px-4 py-3 flex flex-row flex-wrap items-center gap-x-3"
      >
        <div class="mr-auto text-yellow-800">
          <div class="font-semibold text-base">
            {{ $t("message.marketplaces.editor_not_in_amazon") }}
          </div>
          <div class="text-sm mt-1">
            {{
              item
                ? $t("message.marketplaces.editor_not_in_amazon_mapped")
                : $t("message.marketplaces.editor_no_listing")
            }}
          </div>
        </div>
        <ProductMarketplacesPublish
          v-if="canEdit"
          :code="nkrCode"
          @published="onPublished"
        />
      </div>

      <!-- Con la exportación apagada el item existe en Amazon pero ningún envío
           lo incluye: conviene saberlo antes de trabajarlo. -->
      <div
        v-else-if="!item.enabledForExports"
        class="mb-4 text-base text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2"
      >
        {{ $t("message.marketplaces.editor_exports_disabled") }}
      </div>

      <!-- Barra de acciones -->
      <div
        v-if="item"
        class="flex flex-row flex-wrap items-center gap-2 border rounded-lg bg-gray-50 px-4 py-3 mb-5"
      >
        <div class="flex flex-col mr-auto text-base">
          <div class="flex flex-row flex-wrap gap-x-4 text-gray-600">
            <span v-if="item.asin">
              ASIN
              <a
                :href="amazonProductUrl(item.asin)"
                target="_blank"
                rel="noopener"
                class="font-mono text-blue-600 hover:underline"
                >{{ item.asin }}</a
              >
              <button
                type="button"
                class="text-gray-400 hover:text-gray-700 focus:outline-none"
                :title="$t('message.product_images.copy')"
                @click="copyHeaderCode(item.asin)"
              >
                <i
                  :class="[
                    'mdi',
                    copiedCode === item.asin
                      ? 'mdi-check text-green-600'
                      : 'mdi-content-copy'
                  ]"
                ></i>
              </button>
            </span>
            <span>{{ item.fulfillmentChannel }}</span>
            <span v-if="item.price !== null">${{ item.price }}</span>
            <span v-if="item.stock !== null"
              >{{ $t("message.marketplaces.catalog_stock_nkr") }}:
              {{ item.stock }}</span
            >
          </div>
          <div class="text-sm text-gray-400 mt-1">
            <template v-if="amazon && amazon.fetchedAt">
              {{ $t("message.marketplaces.editor_fetched_at") }}:
              {{ amazon.fetchedAt }}
            </template>
            <template v-else>
              {{ $t("message.marketplaces.editor_never_fetched") }}
            </template>
          </div>
          <div
            v-if="amazon && amazon.contentPushedAt"
            class="text-sm text-yellow-700 mt-1"
          >
            <i class="mdi mdi-clock-outline"></i>
            {{
              $t("message.marketplaces.editor_pending_push", {
                date: amazon.contentPushedAt
              })
            }}
          </div>
        </div>

        <a
          :href="amazonEditUrl(item.sku)"
          target="_blank"
          rel="noopener"
          class="btn btn-dark-button"
        >
          <i class="mdi mdi-open-in-new mr-1"></i>
          {{ $t("message.marketplaces.catalog_edit_in_amazon") }}
        </a>
        <button
          class="btn btn-dark-button flex flex-row items-center"
          :disabled="fetching"
          :class="fetching ? 'opacity-50 cursor-not-allowed' : ''"
          @click="fetchFromAmazon"
        >
          <BaseMiniSpinner v-if="fetching" class="mr-2" />
          <i v-else class="mdi mdi-cloud-download mr-1"></i>
          {{ $t("message.marketplaces.editor_fetch_amazon") }}
        </button>
        <button
          v-if="canEdit"
          class="btn btn-info-button flex flex-row items-center"
          :disabled="syncing"
          :class="syncing ? 'opacity-50 cursor-not-allowed' : ''"
          @click="syncNow"
        >
          <BaseMiniSpinner v-if="syncing" class="mr-2" />
          <i v-else class="mdi mdi-wrench mr-1"></i>
          {{ $t("message.marketplaces.editor_sync_now") }}
        </button>
        <button
          v-if="canEdit"
          class="btn flex flex-row items-center"
          :class="item.verifiedAt ? 'btn-dark-button' : 'btn-success-button'"
          :disabled="verifying"
          @click="toggleVerified"
        >
          <BaseMiniSpinner v-if="verifying" class="mr-2" />
          <i v-else class="mdi mdi-check-decagram mr-1"></i>
          {{
            item.verifiedAt
              ? $t("message.marketplaces.editor_unverify")
              : $t("message.marketplaces.editor_verify")
          }}
        </button>
      </div>

      <!-- Tabs: el listing (precio, imágenes, contenido) y A+ son dos sistemas
           distintos de Amazon, con permisos y ciclos de aprobación propios. -->
      <div class="flex flex-row border-b mb-5">
        <button
          v-for="t in tabs"
          :key="t.key"
          class="px-4 py-2 text-base border-b-2 -mb-px"
          :class="
            tab === t.key
              ? 'border-blue-600 text-blue-600 font-semibold'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          "
          @click="selectTab(t.key)"
        >
          {{ t.label }}
        </button>
      </div>

      <div v-show="tab === 'listing'">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <!-- Lo que Amazon tiene publicado -->
          <section class="border rounded-lg p-4">
            <h3 class="font-semibold mb-3">
              {{ $t("message.marketplaces.editor_amazon_side") }}
            </h3>

            <div
              v-if="amazon && amazon.lastError"
              class="mb-3 text-base text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
            >
              {{ amazon.lastError }}
            </div>

            <p v-if="!amazon" class="text-base text-gray-500 py-6 text-center">
              {{ $t("message.marketplaces.editor_fetch_hint") }}
            </p>

            <template v-else>
              <div
                v-if="amazon.images.length"
                class="flex flex-wrap gap-2 mb-4"
              >
                <a
                  v-for="image in amazon.images"
                  :key="image.variant"
                  :href="image.link"
                  target="_blank"
                  rel="noopener"
                  class="border rounded overflow-hidden w-24"
                  :title="`${image.variant} · ${image.width}×${image.height}`"
                >
                  <img
                    :src="image.link"
                    class="w-24 h-24 object-contain bg-gray-100"
                  />
                  <span class="block text-center text-sm text-gray-500 py-0.5">
                    {{ image.variant }}
                  </span>
                </a>
              </div>
              <p v-else class="text-sm text-gray-400 mb-4">
                {{ $t("message.marketplaces.editor_no_amazon_images") }}
              </p>

              <dl class="text-base">
                <dt class="text-gray-500">
                  {{ $t("message.marketplaces.product_title") }}
                </dt>
                <dd class="mb-3">{{ amazon.itemName || "—" }}</dd>
                <dt class="text-gray-500">
                  {{ $t("message.marketplaces.editor_brand") }}
                </dt>
                <dd class="mb-3">{{ amazon.brand || "—" }}</dd>
                <dt class="text-gray-500">
                  {{ $t("message.marketplaces.editor_bullets") }}
                </dt>
                <dd class="mb-3">
                  <ul v-if="amazon.bullets.length" class="list-disc pl-5">
                    <li v-for="(bullet, i) in amazon.bullets" :key="i">
                      {{ bullet }}
                    </li>
                  </ul>
                  <span v-else>—</span>
                </dd>
                <dt class="text-gray-500">
                  {{ $t("message.marketplaces.editor_description") }}
                </dt>
                <dd class="mb-3 whitespace-pre-line">
                  {{ amazon.description || "—" }}
                </dd>
              </dl>

              <button
                v-if="amazon.attributesJson"
                class="text-sm text-blue-600 hover:underline"
                @click="showAttributes = !showAttributes"
              >
                {{
                  showAttributes
                    ? $t("message.marketplaces.editor_hide_attributes")
                    : $t("message.marketplaces.editor_show_attributes")
                }}
              </button>
              <pre
                v-if="showAttributes"
                class="mt-2 text-sm bg-gray-900 text-gray-100 rounded p-3 overflow-auto max-h-64"
                >{{ prettyAttributes }}</pre
              >
            </template>
          </section>

          <!-- Nuestro producto: imágenes editables -->
          <section class="border rounded-lg p-4">
            <div class="flex flex-row items-center justify-between mb-3">
              <h3 class="font-semibold">
                {{ $t("message.marketplaces.editor_our_side") }}
                <a
                  :href="nkrProductUrl"
                  target="_blank"
                  rel="noopener"
                  class="ml-2 text-base font-normal text-blue-600 hover:underline"
                  :title="$t('message.marketplaces.catalog_open_in_nkr')"
                >
                  {{ nkrCode }}
                  <i class="mdi mdi-open-in-new"></i>
                </a>
              </h3>
              <button
                class="text-gray-400 hover:text-gray-700"
                :title="$t('message.marketplaces.editor_reload_images')"
                @click="reloadImages"
              >
                <i class="mdi mdi-refresh"></i>
              </button>
            </div>

            <p class="text-base text-gray-600 mb-3">
              {{ workspace.product.title || "—" }}
            </p>

            <div v-if="loadingImages" class="py-6 flex justify-center">
              <BaseMiniSpinner />
            </div>
            <div
              v-else-if="!dbImages.length"
              class="text-sm text-gray-400 mb-3"
            >
              {{ $t("message.marketplaces.editor_no_images") }}
            </div>
            <div v-else class="flex flex-wrap gap-3 mb-4">
              <div
                v-for="image in dbImages"
                :key="image.id"
                class="border rounded overflow-hidden w-28"
                :class="isMain(image) ? 'border-green-500 border-2' : ''"
              >
                <img
                  :src="image.imageRoute"
                  class="w-full h-24 object-contain bg-gray-100"
                />
                <div class="p-1 text-center">
                  <span v-if="isMain(image)" class="text-sm text-green-600">
                    {{ $t("message.marketplaces.editor_main_image") }}
                  </span>
                  <button
                    v-else-if="canEdit"
                    class="text-sm text-blue-600 hover:underline"
                    :disabled="settingMain"
                    @click="setMain(image)"
                  >
                    {{ $t("message.marketplaces.editor_set_main") }}
                  </button>
                </div>
              </div>
            </div>

            <div v-if="canEdit" class="flex flex-row flex-wrap gap-2 mb-4">
              <button
                class="btn btn-dark-button flex flex-row items-center"
                :disabled="uploading"
                @click="openUpload(false)"
              >
                <BaseMiniSpinner
                  v-if="uploading && !replaceMain"
                  class="mr-2"
                />
                <i v-else class="mdi mdi-upload mr-1"></i>
                {{ $t("message.marketplaces.editor_upload_image") }}
              </button>
              <button
                class="btn btn-dark-button flex flex-row items-center"
                :disabled="uploading"
                @click="openUpload(true)"
              >
                <BaseMiniSpinner v-if="uploading && replaceMain" class="mr-2" />
                <i v-else class="mdi mdi-image-edit mr-1"></i>
                {{ $t("message.marketplaces.editor_replace_main") }}
              </button>
              <router-link
                :to="{
                  name: 'ImageGeneratorPage',
                  query: { nkrCode: nkrCode }
                }"
                target="_blank"
                class="btn btn-dark-button flex flex-row items-center"
              >
                <i class="mdi mdi-auto-fix mr-1"></i>
                {{ $t("message.marketplaces.editor_open_generator") }}
              </router-link>
            </div>

            <FileSelector
              v-model="showFileSelector"
              type="assets"
              subcategory="product_image"
              :extra="nkrCode"
              @fileUploaded="fileUploaded"
              @selectedFile="fileUploaded"
            />

            <!-- Imágenes generadas con IA, con su propio refresh -->
            <ProductAiImages
              ref="aiImages"
              class="mt-2"
              :code="nkrCode"
              :busy-id="addingAiImageId"
              @use-image="useAiImage"
              @loaded="aiImagesLoaded"
            />
          </section>
        </div>
      </div>

      <!-- Actualizar contenido en Amazon: pestaña propia para que la acción
           principal del editor se vea sin scrollear. El borrador se conserva
           al cambiar de pestaña; sólo se reinicia al enviar. -->
      <section
        v-if="tab === 'content' && item && canEdit"
        class="border rounded-lg p-4"
      >
        <h3 class="font-semibold">
          {{ $t("message.marketplaces.editor_content_title") }}
        </h3>

        <template v-if="contentOpen">
          <!-- Paso 2: confirmación. Se ve qué hay hoy en Amazon y qué quedaría,
             antes de escribir sobre un listing vivo. -->
          <div v-if="contentPreview" class="mt-4">
            <div
              v-if="!contentPreview.hasCurrent"
              class="mb-3 text-base text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2"
            >
              {{ $t("message.marketplaces.editor_content_no_baseline") }}
            </div>
            <p v-else class="text-sm text-gray-500 mb-3">
              {{ $t("message.marketplaces.editor_content_baseline") }}:
              {{ contentPreview.fetchedAt }}
            </p>
            <div
              v-if="contentPreview.pendingPush"
              class="mb-3 text-base text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2"
            >
              {{
                $t("message.marketplaces.editor_content_pending_baseline", {
                  date: contentPreview.contentPushedAt
                })
              }}
            </div>

            <table class="w-full text-base mb-4">
              <thead>
                <tr class="border-b text-left text-gray-600">
                  <th class="py-2 pr-3 w-32">
                    {{ $t("message.marketplaces.editor_content_field") }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ $t("message.marketplaces.editor_content_current") }}
                  </th>
                  <th class="py-2">
                    {{ $t("message.marketplaces.editor_content_next") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="change in contentPreview.changes"
                  :key="change.field"
                  class="border-b align-top"
                  :class="change.changed ? '' : 'opacity-50'"
                >
                  <td class="py-2 pr-3 font-semibold">
                    {{ fieldLabel(change.field) }}
                    <span
                      v-if="!change.changed"
                      class="block text-sm font-normal text-gray-400"
                    >
                      {{ $t("message.marketplaces.editor_content_unchanged") }}
                    </span>
                  </td>
                  <td
                    class="py-2 pr-3 whitespace-pre-line text-gray-500 break-words"
                  >
                    {{ change.current || "—" }}
                  </td>
                  <td class="py-2 whitespace-pre-line break-words">
                    {{ change.next || "—" }}
                  </td>
                </tr>
              </tbody>
            </table>

            <button
              class="text-sm text-blue-600 hover:underline"
              @click="showPatches = !showPatches"
            >
              {{
                showPatches
                  ? $t("message.marketplaces.hide_json")
                  : $t("message.marketplaces.view_json")
              }}
            </button>
            <pre
              v-if="showPatches"
              class="mt-2 text-sm bg-gray-900 text-gray-100 rounded p-3 overflow-auto max-h-64"
              >{{ contentPreview.patchesJson }}</pre
            >

            <div class="flex flex-row justify-end gap-2 mt-4">
              <button
                class="btn btn-dark-button"
                :disabled="pushing"
                @click="contentPreview = null"
              >
                {{ $t("message.marketplaces.editor_content_back") }}
              </button>
              <button
                class="btn flex flex-row items-center"
                :class="
                  pushing || !changedCount
                    ? 'btn-dark-button opacity-50 cursor-not-allowed'
                    : 'btn-success-button'
                "
                :disabled="pushing || !changedCount"
                @click="confirmPush"
              >
                <BaseMiniSpinner v-if="pushing" class="mr-2" />
                {{
                  $t("message.marketplaces.editor_content_confirm", {
                    count: changedCount
                  })
                }}
              </button>
            </div>
          </div>

          <!-- Paso 1: qué mandar. Lo no tildado no se toca en Amazon. -->
          <div v-else class="mt-4">
            <div
              class="flex flex-row flex-wrap items-center justify-between gap-2 mb-4"
            >
              <p class="text-sm text-gray-500">
                {{ $t("message.marketplaces.editor_content_hint") }}
              </p>
              <div class="flex flex-row items-center gap-2">
                <button
                  v-if="canUseAi"
                  class="btn flex flex-row items-center"
                  :class="
                    suggesting
                      ? 'btn-dark-button opacity-50 cursor-not-allowed'
                      : 'btn-dark-button'
                  "
                  :disabled="suggesting"
                  @click="suggestContent"
                >
                  <BaseMiniSpinner v-if="suggesting" class="mr-2" />
                  <i v-else class="mdi mdi-creation mr-1"></i>
                  {{ $t("message.marketplaces.editor_content_suggest") }}
                </button>
                <button
                  v-if="draftBackup"
                  class="text-sm text-blue-600 hover:underline"
                  @click="undoSuggestion"
                >
                  {{ $t("message.marketplaces.editor_content_undo") }}
                </button>
              </div>
            </div>
            <p v-if="suggestionNote" class="text-sm text-gray-500 mb-1">
              <i class="mdi mdi-information-outline"></i>
              {{ suggestionNote }}
            </p>
            <div v-if="lastPrompt" class="mb-4">
              <button
                class="text-sm text-blue-600 hover:underline"
                @click="showPrompt = !showPrompt"
              >
                {{
                  showPrompt
                    ? $t("message.marketplaces.editor_hide_prompt")
                    : $t("message.marketplaces.editor_show_prompt")
                }}
              </button>
              <pre
                v-if="showPrompt"
                class="mt-2 text-sm bg-gray-900 text-gray-100 rounded p-3 overflow-auto max-h-96 whitespace-pre-wrap"
                >{{ promptText }}</pre
              >
            </div>

            <div class="mb-4">
              <label class="flex flex-row items-center font-semibold mb-1">
                <input v-model="send.title" type="checkbox" class="mr-2" />
                {{ $t("message.marketplaces.product_title") }}
              </label>
              <input
                v-model="draft.title"
                type="text"
                class="border rounded px-3 py-1 w-full"
                :disabled="!send.title"
              />
            </div>

            <div class="mb-4">
              <label class="flex flex-row items-center font-semibold mb-1">
                <input v-model="send.bullets" type="checkbox" class="mr-2" />
                {{ $t("message.marketplaces.editor_bullets") }}
                <span class="ml-2 text-sm font-normal text-gray-400">
                  {{ $t("message.marketplaces.editor_content_bullets_hint") }}
                </span>
              </label>
              <input
                v-for="(bullet, i) in draft.bullets"
                :key="i"
                v-model="draft.bullets[i]"
                type="text"
                class="border rounded px-3 py-1 w-full mb-1"
                :disabled="!send.bullets"
              />
            </div>

            <div class="mb-4">
              <label class="flex flex-row items-center font-semibold mb-1">
                <input
                  v-model="send.description"
                  type="checkbox"
                  class="mr-2"
                />
                {{ $t("message.marketplaces.editor_description") }}
              </label>
              <textarea
                v-model="draft.description"
                rows="5"
                class="border rounded px-3 py-1 w-full"
                :disabled="!send.description"
              ></textarea>
            </div>

            <div class="mb-4">
              <label class="flex flex-row items-center font-semibold mb-1">
                <input v-model="send.images" type="checkbox" class="mr-2" />
                {{ $t("message.marketplaces.publish_images") }}
                <span class="ml-2 text-sm font-normal text-gray-400">
                  ({{ selectedImageCount }})
                </span>
                <button
                  type="button"
                  class="ml-2 text-gray-400 hover:text-gray-700"
                  :title="$t('message.marketplaces.editor_reload_images')"
                  @click="refreshImageRoles"
                >
                  <i
                    class="mdi mdi-refresh"
                    :class="loadingImages ? 'mdi-spin' : ''"
                  ></i>
                </button>
              </label>
              <p v-if="!imageRoles.length" class="text-sm text-gray-400">
                {{ $t("message.marketplaces.editor_no_images") }}
              </p>
              <div
                v-for="group in imageRoleGroups"
                :key="group.source"
                class="mt-1"
              >
                <p
                  v-if="group.source === 'ai'"
                  class="text-sm text-gray-500 mt-3 mb-1"
                >
                  <i class="mdi mdi-auto-fix"></i>
                  {{ $t("message.product_images.ai_images_title") }}
                </p>
                <div class="flex flex-row flex-wrap gap-3">
                  <div
                    v-for="image in group.images"
                    :key="image.url"
                    class="flex flex-col items-center w-24"
                  >
                    <img
                      :src="image.thumbnail || image.url"
                      class="w-20 h-20 object-contain border rounded bg-gray-100"
                      :class="{
                        'ring-2 ring-green-500': image.role === 'main',
                        'opacity-40': image.role === 'none'
                      }"
                    />
                    <select
                      v-model="image.role"
                      class="border rounded w-full mt-1 text-sm"
                      :disabled="!send.images"
                    >
                      <option value="none">
                        {{ $t("message.marketplaces.image_role_none") }}
                      </option>
                      <option value="other">
                        {{ $t("message.marketplaces.editor_image_role_add") }}
                      </option>
                      <option value="main">
                        {{
                          $t(
                            "message.marketplaces.editor_image_role_replace_main"
                          )
                        }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
              <p v-if="send.images" class="text-sm text-gray-500 mt-2">
                {{ $t("message.marketplaces.editor_images_additive") }}
              </p>
              <p
                v-if="send.images && mainImageCount > 1"
                class="text-sm text-red-500 mt-1"
              >
                {{ $t("message.marketplaces.editor_images_one_main") }}
              </p>
            </div>

            <div class="flex flex-row justify-end">
              <button
                class="btn flex flex-row items-center"
                :class="
                  previewing || !canReview
                    ? 'btn-dark-button opacity-50 cursor-not-allowed'
                    : 'btn-info-button'
                "
                :disabled="previewing || !canReview"
                @click="reviewContent"
              >
                <BaseMiniSpinner v-if="previewing" class="mr-2" />
                {{ $t("message.marketplaces.editor_content_review") }}
              </button>
            </div>
          </div>
        </template>
      </section>

      <!-- A+ Content: documento asociado al ASIN, no campo del listing -->
      <section v-if="tab === 'aplus'" class="border rounded-lg p-4">
        <div v-if="loadingAplus" class="py-10 flex justify-center">
          <BaseMiniSpinner />
        </div>

        <template v-else-if="aplus">
          <div
            v-if="!aplus.status.eligible"
            class="text-base text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-3"
          >
            <p class="font-semibold mb-1">
              {{ $t("message.marketplaces.aplus_not_eligible") }}
            </p>
            <p>{{ aplus.status.message }}</p>
          </div>

          <template v-else>
            <div
              v-if="!aplus.current.asins.length"
              class="text-base text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2 mb-4"
            >
              {{ $t("message.marketplaces.aplus_no_asin") }}
            </div>

            <!-- Lo que hoy cubre a este ASIN -->
            <div class="mb-5">
              <div class="flex flex-row items-center justify-between mb-2">
                <h3 class="font-semibold">
                  {{ $t("message.marketplaces.aplus_current") }}
                </h3>
                <!-- Siempre visible: las imágenes de A+ no se pueden
                     previsualizar acá, así que Seller Central es el único
                     lugar donde ver el contenido como lo ve el comprador. -->
                <a
                  :href="aplusManagerUrl"
                  target="_blank"
                  rel="noopener"
                  class="text-base text-blue-600 hover:underline"
                >
                  {{ $t("message.marketplaces.aplus_open_manager") }}
                  <i class="mdi mdi-open-in-new"></i>
                </a>
              </div>
              <p
                v-if="!aplus.current.documents.length"
                class="text-base text-gray-500"
              >
                {{ $t("message.marketplaces.aplus_none") }}
              </p>
              <table v-else class="w-full text-base">
                <thead>
                  <tr class="border-b text-left text-gray-600">
                    <th class="py-2 pr-3">
                      {{ $t("message.marketplaces.aplus_document") }}
                    </th>
                    <th class="py-2 pr-3">ASIN</th>
                    <th class="py-2 pr-3">
                      {{ $t("message.marketplaces.catalog_state") }}
                    </th>
                    <th class="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="doc in aplus.current.documents"
                    :key="doc.contentReferenceKey + doc.asin"
                    class="border-b"
                  >
                    <td class="py-2 pr-3">{{ doc.name || "—" }}</td>
                    <td class="py-2 pr-3 font-mono">{{ doc.asin }}</td>
                    <td class="py-2 pr-3">
                      <span
                        class="px-2 py-0.5 rounded text-sm"
                        :class="aplusStatusClass(doc.status)"
                        >{{ doc.status || "—" }}</span
                      >
                    </td>
                    <td class="py-2 text-right">
                      <a
                        :href="aplusManagerUrl"
                        target="_blank"
                        rel="noopener"
                        class="text-sm text-gray-500 hover:underline mr-3"
                        >{{ $t("message.marketplaces.aplus_view_amazon") }}</a
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Con un documento ya publicado, crear otro no es la operación
                 natural: lo reemplaza. Se ofrece sólo como salida explícita. -->
            <div
              v-if="canEdit && aplus.current.documents.length && !forceCreate"
              class="text-base text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-3"
            >
              <p class="mb-2">
                <i class="mdi mdi-alert-outline"></i>
                {{ $t("message.marketplaces.aplus_already_has_content") }}
              </p>
              <a
                :href="aplusManagerUrl"
                target="_blank"
                rel="noopener"
                class="btn btn-dark-button inline-block mr-2"
              >
                {{ $t("message.marketplaces.aplus_open_manager") }}
              </a>
              <button
                class="text-sm text-blue-600 hover:underline"
                @click="forceCreate = true"
              >
                {{ $t("message.marketplaces.aplus_create_anyway") }}
              </button>
            </div>

            <!-- Borrador -->
            <template v-else-if="canEdit && aplusDraft">
              <h3 class="font-semibold mb-1">
                {{ $t("message.marketplaces.aplus_new_title") }}
              </h3>
              <p class="text-sm text-gray-500 mb-2">
                {{ $t("message.marketplaces.aplus_hint") }}
              </p>
              <div
                v-if="savedDraft"
                class="text-base text-gray-600 bg-gray-50 border rounded px-3 py-2 mb-3 flex flex-row flex-wrap items-center justify-between gap-2"
              >
                <span>
                  <i class="mdi mdi-content-save-outline"></i>
                  {{
                    $t("message.marketplaces.aplus_draft_restored", {
                      date: savedDraft.updatedAt,
                      user: savedDraft.userName || "—"
                    })
                  }}
                </span>
                <button
                  class="text-sm text-blue-600 hover:underline"
                  @click="discardDraft"
                >
                  {{ $t("message.marketplaces.aplus_discard_draft") }}
                </button>
              </div>

              <!-- Claude arma la estructura y el texto; los prompts que
                   devuelve alimentan al generador de imágenes (Gemini). -->
              <div class="flex flex-row flex-wrap items-center gap-2 mb-3">
                <button
                  v-if="canUseAi"
                  class="btn btn-dark-button flex flex-row items-center"
                  :class="
                    suggestingAplus ? 'opacity-50 cursor-not-allowed' : ''
                  "
                  :disabled="suggestingAplus"
                  @click="suggestAplus"
                >
                  <BaseMiniSpinner v-if="suggestingAplus" class="mr-2" />
                  <i v-else class="mdi mdi-creation mr-1"></i>
                  {{ $t("message.marketplaces.aplus_suggest") }}
                </button>
                <button
                  v-if="aplusBackup"
                  class="text-sm text-blue-600 hover:underline"
                  @click="undoAplusSuggestion"
                >
                  {{ $t("message.marketplaces.editor_content_undo") }}
                </button>
                <button
                  v-if="suggestionsAvailable"
                  class="text-sm text-blue-600 hover:underline"
                  @click="showSuggestions = !showSuggestions"
                >
                  {{
                    $t("message.marketplaces.aplus_previous_batches", {
                      count: suggestions.length
                    })
                  }}
                </button>
                <!-- Sin la tabla no se guarda nada. Mostrarlo es preferible a
                     una lista vacía que parece "nunca generaste". -->
                <span v-else class="text-sm text-yellow-700">
                  <i class="mdi mdi-alert-outline"></i>
                  {{ $t("message.marketplaces.aplus_history_unavailable") }}
                </span>
              </div>

              <!-- Cada corrida del modelo queda guardada: se puede volver a
                   una tanda anterior sin pagar otra llamada. -->
              <div
                v-if="showSuggestions"
                class="border rounded px-3 py-2 mb-4 bg-gray-50"
              >
                <p
                  v-if="!suggestions.length"
                  class="text-sm text-gray-500 py-1"
                >
                  {{ $t("message.marketplaces.aplus_no_batches") }}
                </p>
                <div
                  v-for="batch in suggestions"
                  :key="batch.id"
                  class="flex flex-row flex-wrap items-center justify-between gap-2 border-b last:border-b-0 py-2"
                >
                  <div class="text-sm text-gray-600">
                    <span class="font-semibold">{{ batch.createdAt }}</span>
                    <span v-if="batch.userName"> · {{ batch.userName }}</span>
                    <span class="text-gray-400">
                      ·
                      {{
                        $t("message.marketplaces.aplus_batch_summary", {
                          blocks: (batch.blocks || []).length,
                          images: (batch.imagePrompts || []).length
                        })
                      }}
                    </span>
                  </div>
                  <button
                    class="text-sm text-blue-600 hover:underline"
                    @click="applySuggestion(batch)"
                  >
                    {{ $t("message.marketplaces.aplus_apply_batch") }}
                  </button>
                </div>
              </div>

              <!-- Imágenes que Claude considera que al documento le faltan -->
              <div
                v-if="imagePrompts.length"
                class="border rounded px-3 py-3 mb-4 bg-gray-50"
              >
                <p class="text-base font-semibold mb-1">
                  {{ $t("message.marketplaces.aplus_image_ideas") }}
                </p>
                <p class="text-sm text-gray-500 mb-2">
                  {{ $t("message.marketplaces.aplus_image_ideas_hint") }}
                </p>
                <div
                  v-for="(idea, i) in imagePrompts"
                  :key="i"
                  class="border-t pt-2 mt-2 first:border-t-0 first:pt-0 first:mt-0"
                >
                  <p class="text-base font-semibold">
                    {{ idea.title }}
                    <span
                      v-if="idea.width"
                      class="ml-2 font-normal text-sm text-gray-500"
                    >
                      {{ idea.width }} × {{ idea.height }} px ·
                      {{ idea.aspectRatio }}
                      <template v-if="idea.imageIndex !== undefined">
                        · #{{ idea.imageIndex + 1 }}
                      </template>
                    </span>
                  </p>
                  <p class="text-sm text-gray-600 mb-1">{{ idea.prompt }}</p>
                  <a
                    :href="generatorUrl(idea)"
                    target="_blank"
                    rel="noopener"
                    class="text-sm text-blue-600 hover:underline"
                  >
                    <i class="mdi mdi-auto-fix"></i>
                    {{ $t("message.marketplaces.aplus_generate_image") }}
                  </a>
                </div>
              </div>

              <div class="mb-3">
                <label class="font-semibold text-base">{{
                  $t("message.marketplaces.aplus_name")
                }}</label>
                <input
                  v-model="aplusDraft.name"
                  type="text"
                  class="border rounded px-3 py-1 w-full"
                />
              </div>

              <!-- Imágenes disponibles. Se eligen una vez y los bloques las
                   referencian por posición, que es como Amazon las ordena. -->
              <div class="mb-4">
                <label class="font-semibold text-base">
                  {{ $t("message.marketplaces.aplus_images") }}
                  <span class="font-normal text-sm text-gray-400">
                    {{ $t("message.marketplaces.aplus_images_pick") }}
                  </span>
                </label>
                <p v-if="!dbImages.length" class="text-sm text-gray-400">
                  {{ $t("message.marketplaces.editor_no_images") }}
                </p>
                <div v-else class="flex flex-row flex-wrap gap-3 mt-1">
                  <div
                    v-for="image in dbImages"
                    :key="image.id"
                    class="w-20 cursor-pointer"
                    @click="toggleAplusImage(image)"
                  >
                    <img
                      :src="image.thumbnail || image.imageRoute"
                      class="w-20 h-20 object-contain border rounded bg-gray-100"
                      :class="
                        aplusImageIndex(image) >= 0
                          ? 'ring-2 ring-blue-500'
                          : 'opacity-50'
                      "
                    />
                    <span
                      v-if="aplusImageIndex(image) >= 0"
                      class="block text-center text-sm text-blue-600 font-semibold"
                    >
                      #{{ aplusImageIndex(image) + 1 }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Bloques del documento -->
              <label class="font-semibold text-base">
                {{ $t("message.marketplaces.aplus_blocks") }}
                <span class="font-normal text-sm text-gray-400">
                  ({{ aplusDraft.blocks.length }})
                </span>
              </label>
              <p
                v-if="!aplusDraft.blocks.length"
                class="text-sm text-gray-400 mb-2"
              >
                {{ $t("message.marketplaces.aplus_blocks_empty") }}
              </p>

              <div
                v-for="(block, i) in aplusDraft.blocks"
                :key="i"
                class="border rounded p-3 mb-3"
              >
                <div class="flex flex-row items-center justify-between mb-2">
                  <span class="text-base font-semibold">
                    {{ blockLabel(block.type) }}
                  </span>
                  <div class="flex flex-row items-center gap-2 text-gray-400">
                    <button
                      :disabled="i === 0"
                      :class="i === 0 ? 'opacity-30' : 'hover:text-gray-700'"
                      @click="moveBlock(i, -1)"
                    >
                      <i class="mdi mdi-arrow-up"></i>
                    </button>
                    <button
                      :disabled="i === aplusDraft.blocks.length - 1"
                      :class="
                        i === aplusDraft.blocks.length - 1
                          ? 'opacity-30'
                          : 'hover:text-gray-700'
                      "
                      @click="moveBlock(i, 1)"
                    >
                      <i class="mdi mdi-arrow-down"></i>
                    </button>
                    <button
                      class="hover:text-red-600"
                      @click="aplusDraft.blocks.splice(i, 1)"
                    >
                      <i class="mdi mdi-close"></i>
                    </button>
                  </div>
                </div>

                <input
                  v-model="block.headline"
                  type="text"
                  class="border rounded px-2 py-1 w-full mb-2"
                  :placeholder="$t('message.marketplaces.aplus_headline')"
                />

                <textarea
                  v-if="block.type !== 'specs' && block.type !== 'images'"
                  v-model="block.body"
                  rows="3"
                  class="border rounded px-2 py-1 w-full mb-2"
                  :placeholder="$t('message.marketplaces.aplus_body')"
                ></textarea>

                <template v-if="block.type === 'highlights'">
                  <input
                    v-for="(bullet, b) in block.bullets"
                    :key="b"
                    v-model="block.bullets[b]"
                    type="text"
                    class="border rounded px-2 py-1 w-full mb-1"
                    :placeholder="$t('message.marketplaces.aplus_bullet')"
                  />
                  <button
                    v-if="block.bullets.length < 8"
                    class="text-sm text-blue-600 hover:underline"
                    @click="block.bullets.push('')"
                  >
                    {{ $t("message.marketplaces.aplus_add_bullet") }}
                  </button>
                </template>

                <template v-if="block.type === 'specs'">
                  <p
                    v-if="blockSpecsInvalid(block)"
                    class="text-sm text-red-500 mb-1"
                  >
                    {{ $t("message.marketplaces.aplus_specs_range") }}
                  </p>
                  <div
                    v-for="(row, r) in block.specs"
                    :key="r"
                    class="flex flex-row gap-2 mb-1"
                  >
                    <input
                      v-model="row.label"
                      type="text"
                      class="border rounded px-2 py-1 w-1/3"
                    />
                    <input
                      v-model="row.value"
                      type="text"
                      class="border rounded px-2 py-1 flex-1"
                    />
                    <button
                      class="text-gray-400 hover:text-red-600 px-2"
                      @click="block.specs.splice(r, 1)"
                    >
                      <i class="mdi mdi-close"></i>
                    </button>
                  </div>
                  <div class="flex flex-row gap-3">
                    <button
                      class="text-sm text-blue-600 hover:underline"
                      @click="block.specs.push({ label: '', value: '' })"
                    >
                      {{ $t("message.marketplaces.aplus_add_spec") }}
                    </button>
                    <button
                      v-if="productSpecs.length"
                      class="text-sm text-blue-600 hover:underline"
                      @click="fillSpecsFromProduct(block)"
                    >
                      {{
                        $t("message.marketplaces.aplus_fill_from_product", {
                          count: productSpecs.length
                        })
                      }}
                    </button>
                  </div>
                </template>

                <div
                  v-if="blockImageCount(block.type)"
                  class="flex flex-row flex-wrap items-center gap-2 mt-2"
                >
                  <span class="text-sm text-gray-500">
                    {{ $t("message.marketplaces.aplus_block_images_label") }}
                    <span class="text-gray-400">
                      ({{ blockImageSize(block) }})
                    </span>
                  </span>
                  <button
                    v-for="n in aplusImages.length"
                    :key="n"
                    class="text-sm border rounded px-2 py-0.5"
                    :class="
                      blockUsesImage(block, n - 1)
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-600'
                    "
                    @click="toggleBlockImage(block, n - 1)"
                  >
                    #{{ n }}
                  </button>
                  <span
                    v-if="!aplusImages.length"
                    class="text-sm text-gray-400"
                  >
                    {{ $t("message.marketplaces.aplus_pick_images_first") }}
                  </span>
                </div>
              </div>

              <div class="flex flex-row flex-wrap items-center gap-2 mb-4">
                <span class="text-sm text-gray-500">
                  {{ $t("message.marketplaces.aplus_add_block") }}
                </span>
                <button
                  v-for="type in blockTypes"
                  :key="type"
                  class="text-sm border rounded px-2 py-0.5 hover:bg-gray-100"
                  @click="addBlock(type)"
                >
                  + {{ blockLabel(type) }}
                </button>
              </div>

              <div class="flex flex-row justify-end gap-2">
                <button
                  class="btn btn-dark-button flex flex-row items-center"
                  :class="
                    specsRowsInvalid ? 'opacity-50 cursor-not-allowed' : ''
                  "
                  :disabled="savingAplus || specsRowsInvalid"
                  @click="saveAplus(false)"
                >
                  <BaseMiniSpinner v-if="savingAplus" class="mr-2" />
                  {{ $t("message.marketplaces.aplus_create_draft") }}
                </button>
                <button
                  class="btn flex flex-row items-center"
                  :class="
                    savingAplus || specsRowsInvalid
                      ? 'btn-dark-button opacity-50 cursor-not-allowed'
                      : 'btn-success-button'
                  "
                  :disabled="savingAplus || specsRowsInvalid"
                  @click="saveAplus(true)"
                >
                  <BaseMiniSpinner v-if="savingAplus" class="mr-2" />
                  {{ $t("message.marketplaces.aplus_create_submit") }}
                </button>
              </div>
            </template>
          </template>
        </template>
      </section>

      <!-- Bitácora -->
      <section class="border rounded-lg p-4 mt-5">
        <h3 class="font-semibold mb-3">
          {{ $t("message.marketplaces.editor_log") }}
        </h3>
        <p v-if="!workspace.actions.length" class="text-base text-gray-400">
          {{ $t("message.marketplaces.editor_log_empty") }}
        </p>
        <table v-else class="w-full text-base">
          <tbody>
            <tr
              v-for="entry in workspace.actions"
              :key="entry.id"
              class="border-b"
            >
              <td class="py-1 pr-3 whitespace-no-wrap text-gray-500">
                {{ entry.createdAt }}
              </td>
              <td class="py-1 pr-3">
                <span class="px-2 py-0.5 rounded text-sm bg-gray-100">
                  {{ actionLabel(entry.action) }}
                </span>
              </td>
              <td class="py-1 pr-3">{{ entry.detail }}</td>
              <td class="py-1 text-right text-gray-500">
                {{ entry.userName || "—" }}
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  </BaseModal>
</template>

<script>
import FileSelector from "@/components/FileSelector.vue";
import ProductAiImages from "@/components/product/ProductAiImages.vue";
import ProductMarketplacesPublish from "@/components/product/ProductMarketplacesPublish.vue";
import { getProductImages } from "@/api/store/ProductsService.js";
import {
  suggestAmazonContent,
  suggestAplusContent
} from "@/api/store/AiService.js";
import {
  saveImageToProduct,
  setMainImageApi
} from "@/api/masterAdmin/ProductImagesService.js";
import { debounce } from "lodash";
import {
  getAmazonAplusForProduct,
  saveAmazonAplus,
  saveAmazonAplusDraft,
  getAmazonListingWorkspace,
  fetchAmazonListingDetails,
  setAmazonItemVerified,
  logAmazonItemAction,
  syncToAmazon,
  previewAmazonContent,
  pushAmazonContent
} from "@/api/store/MarketplacesItemsService.js";

const SELLER_CENTRAL = "https://sellercentral.amazon.com";

export default {
  name: "AmazonListingEditor",
  components: { FileSelector, ProductAiImages, ProductMarketplacesPublish },
  props: {
    nkrCode: {
      type: String,
      required: true
    },
    // SKU de la fila desde la que se abrió: si el producto tiene varios
    // listings, arranca en el que el usuario estaba mirando.
    sku: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      workspace: null,
      copiedCode: null,
      loading: true,
      activeIndex: 0,
      dbImages: [],
      aiImages: [],
      loadingImages: false,
      fetching: false,
      syncing: false,
      verifying: false,
      uploading: false,
      settingMain: false,
      showFileSelector: false,
      // La subida es la misma; el flag decide si además se marca como principal.
      replaceMain: false,
      addingAiImageId: null,
      showAttributes: false,
      // Actualización de contenido. `send` decide qué campos viajan: lo que no
      // está tildado no se toca en Amazon.
      contentOpen: false,
      send: { title: false, bullets: false, description: false, images: false },
      draft: { title: "", bullets: ["", "", "", "", ""], description: "" },
      imageRoles: [],
      previewing: false,
      pushing: false,
      contentPreview: null,
      showPatches: false,
      suggesting: false,
      // Copia previa a la sugerencia, para poder volver atrás sin perder lo
      // que el usuario hubiera escrito a mano.
      draftBackup: null,
      suggestionNote: null,
      // Lo que se le mandó al modelo en la última sugerencia.
      lastPrompt: null,
      showPrompt: false,
      // A+ Content
      tab: "listing",
      aplus: null,
      aplusDraft: null,
      loadingAplus: false,
      savingAplus: false,
      // contentReferenceKey cuando se edita un documento existente en vez de
      // crear uno nuevo.
      aplusImages: [],
      forceCreate: false,
      suggestingAplus: false,
      aplusBackup: null,
      imagePrompts: [],
      // Borrador persistido y tandas anteriores del modelo.
      savedDraft: null,
      suggestions: [],
      suggestionsAvailable: true,
      showSuggestions: false
    };
  },
  computed: {
    canEdit() {
      return this.$store.getters["auth/userHasPermission"](
        "can_edit_marketplaces_items"
      );
    },
    canUseAi() {
      return this.$store.getters["auth/userHasPermission"](
        "can_use_ia_features"
      );
    },
    item() {
      if (!this.workspace || !this.workspace.items.length) return null;
      return this.workspace.items[this.activeIndex] || null;
    },
    amazon() {
      return this.item ? this.item.amazon : null;
    },
    // No esta publicado: o no hay registro en marketplaces_items, o lo hay pero
    // sin ASIN, que es como queda una pieza que nunca llego a Amazon.
    notInAmazon() {
      return !this.item || !this.item.asin;
    },
    // Ficha completa de la pieza en el panel, para abrirla en otra pestaña.
    nkrProductUrl() {
      return this.$router.resolve({
        name: "ProductPage",
        params: { productCode: this.nkrCode }
      }).href;
    },
    imageRoleGroups() {
      return ["product", "ai"]
        .map(source => ({
          source,
          images: this.imageRoles.filter(i => i.source === source)
        }))
        .filter(group => group.images.length);
    },
    selectedImageCount() {
      return this.imageRoles.filter(i => i.role !== "none").length;
    },
    mainImageCount() {
      return this.imageRoles.filter(i => i.role === "main").length;
    },
    // Se puede revisar si hay al menos un campo tildado, y si al mandar
    // imágenes la selección es válida (Amazon acepta exactamente una principal).
    canReview() {
      const any = Object.keys(this.send).some(k => this.send[k]);
      if (!any) return false;
      // Las adicionales se suman a lo publicado, así que no hace falta que haya
      // una principal; lo único inválido es elegir dos.
      if (this.send.images && this.mainImageCount > 1) return false;
      return true;
    },
    aplusManagerUrl() {
      return `${SELLER_CENTRAL}/enhanced-content/content-manager`;
    },
    blockTypes() {
      return ["header", "highlights", "specs", "images", "text"];
    },
    // Las filas que el backend armó con las specs y el fitment del producto.
    productSpecs() {
      if (!this.aplus || !this.aplus.draft) return [];
      const specs = (this.aplus.draft.blocks || []).find(
        b => b.type === "specs"
      );
      return specs ? specs.specs || [] : [];
    },
    // Ningún bloque puede quedar con una tabla fuera del rango de Amazon.
    specsRowsInvalid() {
      if (!this.aplusDraft) return false;
      return this.aplusDraft.blocks.some(block =>
        this.blockSpecsInvalid(block)
      );
    },
    tabs() {
      return [
        { key: "listing", label: this.$t("message.marketplaces.tab_listing") },
        this.item && this.canEdit
          ? {
              key: "content",
              label: this.$t("message.marketplaces.tab_content")
            }
          : null,
        { key: "aplus", label: this.$t("message.marketplaces.tab_aplus") }
      ].filter(Boolean);
    },
    promptText() {
      if (!this.lastPrompt) return "";
      return [
        `MODEL: ${this.lastPrompt.model}`,
        "",
        "--- SYSTEM ---",
        this.lastPrompt.system,
        "",
        "--- CONTEXT SENT ---",
        this.lastPrompt.context
      ].join("\n");
    },
    changedCount() {
      if (!this.contentPreview) return 0;
      return this.contentPreview.changes.filter(c => c.changed).length;
    },
    // Los atributos llegan como string a propósito (los nombres de Amazon son
    // snake_case y la camelización del cliente los rompería).
    prettyAttributes() {
      if (!this.amazon || !this.amazon.attributesJson) return "";
      try {
        return JSON.stringify(JSON.parse(this.amazon.attributesJson), null, 2);
      } catch (e) {
        return this.amazon.attributesJson;
      }
    }
  },
  watch: {
    // Autoguardado del borrador de A+. Profundo porque los bloques se editan
    // campo a campo, y con debounce para no escribir en cada tecla.
    aplusDraft: {
      deep: true,
      handler() {
        if (this.aplusDraft && this.tab === "aplus") this.queueDraftSave();
      }
    },
    aplusImages() {
      if (this.aplusDraft && this.tab === "aplus") this.queueDraftSave();
    }
  },
  created() {
    this.queueDraftSave = debounce(() => this.persistDraft(), 1500);
  },
  mounted() {
    this.load();
    this.reloadImages();
  },
  beforeDestroy() {
    // Un cierre rápido no debería perder el último cambio.
    if (this.queueDraftSave) this.queueDraftSave.flush();
  },
  methods: {
    close() {
      this.$emit("close");
    },
    async copyHeaderCode(code) {
      if (!code) return;
      try {
        await navigator.clipboard.writeText(code);
      } catch (e) {
        const el = document.createElement("textarea");
        el.value = code;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      this.copiedCode = code;
      setTimeout(() => {
        if (this.copiedCode === code) this.copiedCode = null;
      }, 1500);
    },
    amazonEditUrl(sku) {
      return `${SELLER_CENTRAL}/skucentral?mSku=${encodeURIComponent(sku)}`;
    },
    amazonProductUrl(asin) {
      return `https://www.amazon.com/dp/${encodeURIComponent(asin)}`;
    },
    isMain(image) {
      return image.main && image.main !== "0";
    },
    actionLabel(action) {
      const key = `message.marketplaces.editor_action_${action}`;
      const label = this.$t(key);
      // vue-i18n devuelve la clave cuando no existe: mejor mostrar el código
      // crudo que una ruta de traducción.
      return label === key ? action : label;
    },
    async load() {
      this.loading = true;
      const res = await getAmazonListingWorkspace(this.nkrCode).catch(
        () => null
      );
      this.workspace = res;
      if (res && this.sku) {
        const index = res.items.findIndex(i => i.sku === this.sku);
        if (index >= 0) this.activeIndex = index;
      }
      this.loading = false;
    },
    // Recien creado el listing, el workspace cambia entero: aparece el item con
    // su SKU y su ASIN, y el editor pasa a tener algo que editar. El padre
    // tambien necesita enterarse: la fila del cruce quedo desactualizada.
    onPublished() {
      this.load();
      this.$emit("updated");
    },
    async reloadImages() {
      this.loadingImages = true;
      this.dbImages =
        (await getProductImages(this.nkrCode).catch(() => [])) || [];
      this.loadingImages = false;
      // El panel de contenido puede estar abierto mientras se agregan imágenes:
      // su selector tiene que reflejarlas sin perder lo ya tildado.
      if (this.contentOpen) this.syncImageRoles();
    },
    toast(message, type = "success") {
      this.$toast.open({ message, type, position: "top-right" });
    },
    apiError(e) {
      return (
        (e && e.response && e.response.data) ||
        this.$t("message.marketplaces.sync_error")
      );
    },
    fetchFromAmazon() {
      if (this.fetching || !this.item) return;
      this.fetching = true;
      fetchAmazonListingDetails(this.item.sku, this.item.asin)
        .then(res => {
          if (res && res.error) {
            this.toast(res.error, "error");
          } else {
            this.toast(this.$t("message.marketplaces.editor_fetched"));
          }
          // Recarga completa: la ficha cambió y la bitácora tiene una entrada más.
          this.load();
        })
        .catch(e => this.toast(this.apiError(e), "error"))
        .finally(() => {
          this.fetching = false;
        });
    },
    syncNow() {
      if (this.syncing) return;
      this.syncing = true;
      syncToAmazon(this.nkrCode)
        .then(() => {
          this.toast(this.$t("message.marketplaces.sync_success"));
          this.$emit("updated");
          this.load();
        })
        .catch(e => this.toast(this.apiError(e), "error"))
        .finally(() => {
          this.syncing = false;
        });
    },
    toggleVerified() {
      if (this.verifying || !this.item) return;
      this.verifying = true;
      setAmazonItemVerified(this.item.id, !this.item.verifiedAt)
        .then(() => {
          this.$emit("updated");
          this.load();
        })
        .catch(e => this.toast(this.apiError(e), "error"))
        .finally(() => {
          this.verifying = false;
        });
    },
    // -- A+ Content ---------------------------------------------------------
    selectTab(key) {
      this.tab = key;
      if (key === "content" && !this.contentOpen) {
        this.contentOpen = true;
        this.initContentDraft();
      }
      // Se carga al entrar y no al abrir el modal: consulta a Amazon y la
      // mayoría de las veces el usuario viene a mirar precio o imágenes.
      if (key === "aplus" && !this.aplus && !this.loadingAplus) {
        this.loadAplus();
      }
    },
    async loadAplus() {
      this.loadingAplus = true;
      const res = await getAmazonAplusForProduct(this.nkrCode).catch(e => {
        this.toast(this.apiError(e), "error");
        return null;
      });
      this.aplus = res;
      if (res) {
        this.savedDraft = res.saved || null;
        this.suggestions = res.suggestions || [];
        this.suggestionsAvailable = res.suggestionsAvailable !== false;
        // Lo guardado gana sobre el borrador derivado del producto: es trabajo
        // que alguien ya hizo. El derivado queda disponible para "empezar de
        // cero" y para rellenar la tabla de specs.
        const base = res.saved || res.draft || {};
        this.aplusDraft = {
          name: base.name || (res.draft && res.draft.name) || "",
          blocks: this.cloneBlocks(base.blocks || [])
        };
        if (res.saved) {
          this.aplusImages = (res.saved.images || []).slice();
          this.imagePrompts = res.saved.imagePrompts || [];
        }
      }
      this.loadingAplus = false;
    },
    // Claude propone estructura, texto y qué imágenes faltan. Las imágenes no
    // las genera él: devuelve prompts que abren el generador (Gemini) ya
    // cargados, y la imagen entra al documento por el mismo camino que
    // cualquier otra del producto.
    suggestAplus() {
      if (this.suggestingAplus) return;
      this.suggestingAplus = true;
      suggestAplusContent(this.nkrCode)
        .then(res => {
          this.aplusBackup = JSON.parse(JSON.stringify(this.aplusDraft));
          this.aplusDraft = {
            name: res.name || this.aplusDraft.name,
            blocks: this.cloneBlocks(res.blocks || [])
          };
          this.imagePrompts = res.imagePrompts || [];
          // La tanda ya quedó guardada del lado del backend; se antepone acá
          // para no tener que recargar todo el tab.
          this.suggestions.unshift({
            id: res.suggestionId,
            blocks: res.blocks,
            imagePrompts: res.imagePrompts,
            prompt: res.prompt,
            model: res.prompt && res.prompt.model,
            createdAt: this.$t("message.marketplaces.aplus_just_now"),
            userName: null
          });
          this.persistDraft("ai");
        })
        .catch(e => this.toast(this.apiError(e), "error"))
        .finally(() => {
          this.suggestingAplus = false;
        });
    },
    // Reaplica una tanda anterior sin volver a llamar al modelo.
    applySuggestion(suggestion) {
      this.aplusBackup = JSON.parse(JSON.stringify(this.aplusDraft));
      this.aplusDraft.blocks = this.cloneBlocks(suggestion.blocks || []);
      this.imagePrompts = suggestion.imagePrompts || [];
      this.showSuggestions = false;
      this.persistDraft("ai");
    },
    // Autoguardado: el panel no tiene botón de "guardar borrador" porque el
    // borrador no es una decisión, es el estado de lo que estás editando.
    persistDraft(source) {
      if (!this.aplusDraft) return;
      return saveAmazonAplusDraft(this.nkrCode, {
        name: this.aplusDraft.name,
        blocks: this.aplusDraft.blocks,
        images: this.aplusImages,
        imagePrompts: this.imagePrompts,
        source: source || "manual"
      }).catch(() => {
        // Perder un autoguardado no puede interrumpir la edición.
      });
    },
    discardDraft() {
      if (
        !window.confirm(this.$t("message.marketplaces.aplus_discard_confirm"))
      )
        return;
      saveAmazonAplusDraft(this.nkrCode, null)
        .then(() => {
          this.savedDraft = null;
          this.aplusImages = [];
          this.imagePrompts = [];
          this.aplus = null;
          this.loadAplus();
        })
        .catch(e => this.toast(this.apiError(e), "error"));
    },
    undoAplusSuggestion() {
      if (!this.aplusBackup) return;
      this.aplusDraft = JSON.parse(JSON.stringify(this.aplusBackup));
      this.aplusBackup = null;
      this.imagePrompts = [];
    },
    // Las medidas vienen del backend, no del modelo: Amazon las fija por tipo
    // de módulo. Se pasan al generador para no tener que ajustarlas a mano.
    generatorUrl(idea) {
      const query = { nkrCode: this.nkrCode, prompt: idea.prompt };
      if (idea.aspectRatio) query.aspectRatio = idea.aspectRatio;
      return this.$router.resolve({
        name: "ImageGeneratorPage",
        query
      }).href;
    },
    // Normaliza un bloque venga de donde venga (backend, Claude o el botón de
    // agregar), para que el template no tenga que defenderse de campos ausentes.
    cloneBlocks(blocks) {
      return blocks.map(block => ({
        type: block.type,
        headline: block.headline || "",
        body: block.body || "",
        bullets: (block.bullets || []).slice(),
        specs: (block.specs || []).map(row => ({ ...row })),
        imageIndex: block.imageIndex === undefined ? -1 : block.imageIndex,
        imageIndexes: (block.imageIndexes || []).slice()
      }));
    },
    addBlock(type) {
      this.aplusDraft.blocks.push(
        this.cloneBlocks([
          { type, bullets: type === "highlights" ? [""] : [] }
        ])[0]
      );
    },
    moveBlock(index, delta) {
      const target = index + delta;
      if (target < 0 || target >= this.aplusDraft.blocks.length) return;
      const blocks = this.aplusDraft.blocks;
      blocks.splice(target, 0, blocks.splice(index, 1)[0]);
    },
    blockLabel(type) {
      return this.$t(`message.marketplaces.aplus_block_${type}`);
    },
    // Cuántas imágenes admite el bloque: 0 = no lleva, 1 = una, 4 = una fila.
    blockImageCount(type) {
      if (type === "header" || type === "highlights") return 1;
      if (type === "images") return 4;
      return 0;
    },
    // Medida que Amazon espera para las imágenes de este bloque.
    blockImageSize(block) {
      if (block.type === "header") return "970 × 600 px";
      if (block.type === "images" && block.imageIndexes.length >= 4) {
        return "220 × 220 px";
      }
      return "300 × 300 px";
    },
    blockUsesImage(block, index) {
      return this.blockImageCount(block.type) === 1
        ? block.imageIndex === index
        : block.imageIndexes.indexOf(index) >= 0;
    },
    toggleBlockImage(block, index) {
      if (this.blockImageCount(block.type) === 1) {
        block.imageIndex = block.imageIndex === index ? -1 : index;
        return;
      }
      const at = block.imageIndexes.indexOf(index);
      if (at >= 0) block.imageIndexes.splice(at, 1);
      else if (block.imageIndexes.length < 4) block.imageIndexes.push(index);
    },
    // Amazon rechaza el documento entero con 1 a 3 filas.
    blockSpecsInvalid(block) {
      if (block.type !== "specs") return false;
      const filled = block.specs.filter(
        row => (row.label || "").trim() && (row.value || "").trim()
      ).length;
      return filled > 0 && filled < 4;
    },
    // Repuebla la tabla de un bloque con las specs y el fitment del producto.
    fillSpecsFromProduct(block) {
      block.specs = this.productSpecs.map(row => ({ ...row }));
    },
    aplusStatusClass(status) {
      if (status === "APPROVED") return "bg-green-100 text-green-700";
      if (status === "REJECTED") return "bg-red-100 text-red-700";
      if (status === "SUBMITTED") return "bg-yellow-100 text-yellow-700";
      return "bg-gray-100 text-gray-600";
    },
    // El orden de selección importa: la primera es el hero del encabezado y el
    // resto va a la grilla de tres.
    aplusImageIndex(image) {
      return this.aplusImages.indexOf(image.imageRoute);
    },
    toggleAplusImage(image) {
      const index = this.aplusImageIndex(image);
      if (index >= 0) this.aplusImages.splice(index, 1);
      else this.aplusImages.push(image.imageRoute);
    },
    saveAplus(submit) {
      if (this.savingAplus) return;
      const hasExisting =
        this.aplus &&
        this.aplus.current &&
        this.aplus.current.documents.length > 0;
      const message = hasExisting
        ? this.$t("message.marketplaces.aplus_submit_confirm_replace")
        : this.$t("message.marketplaces.aplus_submit_confirm");
      if (submit && !window.confirm(message)) return;
      this.savingAplus = true;
      // Sin contentReferenceKey: desde el panel solo se crean documentos
      // nuevos. Actualizar uno existente sería a ciegas (ver el aviso).
      saveAmazonAplus({
        nkrCode: this.nkrCode,
        submit,
        draft: { ...this.aplusDraft, images: this.aplusImages }
      })
        .then(res => {
          this.toast(
            submit
              ? this.$t("message.marketplaces.aplus_submitted", {
                  count: res.imagesUploaded
                })
              : this.$t("message.marketplaces.aplus_saved", {
                  count: res.imagesUploaded
                })
          );
          this.aplusImages = [];
          // El documento ya existe en Amazon: el borrador local cumplió.
          saveAmazonAplusDraft(this.nkrCode, null).catch(() => {});
          this.savedDraft = null;
          this.aplus = null;
          this.loadAplus();
          // La acción quedó en la bitácora del backend.
          this.load();
        })
        .catch(e => this.toast(this.apiError(e), "error"))
        .finally(() => {
          this.savingAplus = false;
        });
    },
    // -- Actualización de contenido ----------------------------------------
    // El borrador arranca con lo que Amazon YA tiene publicado, y solo cae a lo
    // nuestro cuando Amazon no trae nada: se edita sobre lo vivo, que es contra
    // lo que después se compara en la confirmación.
    initContentDraft() {
      const ours = this.workspace.product;
      const theirs = this.amazon;
      this.draft.title = (theirs && theirs.itemName) || ours.title || "";
      this.draft.description =
        (theirs && theirs.description) || ours.description || "";
      const bullets = (theirs && theirs.bullets) || [];
      this.draft.bullets = [0, 1, 2, 3, 4].map(i => bullets[i] || "");
      this.syncImageRoles();
      this.contentPreview = null;
      this.showPatches = false;
      this.draftBackup = null;
      this.suggestionNote = null;
      this.lastPrompt = null;
      this.showPrompt = false;
    },
    // Reconstruye la lista de imágenes a enviar a partir de las del producto,
    // conservando los roles ya elegidos. Se llama cada vez que cambian las
    // imágenes (subida, generada con IA, cambio de principal) para que una foto
    // agregada con el panel abierto aparezca sin tener que cerrarlo.
    // Ninguna nueva viene preseleccionada: sumar una imagen a un listing vivo
    // es una decisión, no un default.
    syncImageRoles() {
      const previous = {};
      this.imageRoles.forEach(image => {
        previous[image.url] = image.role;
      });
      const productImages = this.dbImages.map(image => ({
        source: "product",
        url: image.imageRoute,
        thumbnail: image.thumbnail,
        role: previous[image.imageRoute] || "none"
      }));
      // Las generadas con IA se pueden mandar directo a Amazon sin pasar antes
      // por las imágenes del producto. Si ya se agregó, queda sólo arriba.
      const productUrls = productImages.map(image => image.url);
      const aiImages = this.aiImages
        .filter(image => !productUrls.includes(image.url))
        .map(image => ({
          source: "ai",
          url: image.url,
          thumbnail: null,
          role: previous[image.url] || "none"
        }));
      this.imageRoles = productImages.concat(aiImages);
    },
    aiImagesLoaded(images) {
      this.aiImages = images;
      if (this.contentOpen) this.syncImageRoles();
    },
    async refreshImageRoles() {
      if (this.$refs.aiImages) this.$refs.aiImages.load();
      await this.reloadImages();
      this.syncImageRoles();
    },
    // Pide a Claude título, bullets y descripción para este listing. El
    // contexto (ficha, OE y aplicaciones de vehículo) lo arma el backend; acá
    // se manda lo que hay hoy para que mejore sobre eso en vez de reescribir.
    suggestContent() {
      if (this.suggesting) return;
      this.suggesting = true;
      this.suggestionNote = null;
      suggestAmazonContent({
        nkrCode: this.nkrCode,
        currentTitle: this.draft.title,
        currentBullets: this.draft.bullets,
        currentDescription: this.draft.description
      })
        .then(res => {
          this.draftBackup = JSON.parse(JSON.stringify(this.draft));
          this.draft.title = res.title || this.draft.title;
          this.draft.description = res.description || this.draft.description;
          const bullets = res.bullets || [];
          this.draft.bullets = [0, 1, 2, 3, 4].map(i => bullets[i] || "");
          // Se tildan los tres campos: pedir la sugerencia es querer mandarla.
          // Las imágenes no se tocan, son una decisión aparte.
          this.send.title = true;
          this.send.bullets = true;
          this.send.description = true;
          this.lastPrompt = res.prompt || null;
          this.suggestionNote = res.applicationsUsed
            ? this.$t("message.marketplaces.editor_content_suggest_done", {
                count: res.applicationsUsed,
                specs: res.specsUsed || 0
              })
            : this.$t("message.marketplaces.editor_content_suggest_no_apps");
        })
        .catch(e => this.toast(this.apiError(e), "error"))
        .finally(() => {
          this.suggesting = false;
        });
    },
    undoSuggestion() {
      if (!this.draftBackup) return;
      this.draft = JSON.parse(JSON.stringify(this.draftBackup));
      this.draftBackup = null;
      this.suggestionNote = null;
    },
    // Solo viajan las claves tildadas: una clave ausente significa "no tocar
    // ese campo", y mandarlas todas convertiría cualquier envío en un reemplazo.
    contentPayload() {
      const payload = { itemId: this.item.id };
      if (this.send.title) payload.title = this.draft.title;
      if (this.send.description) payload.description = this.draft.description;
      if (this.send.bullets) payload.bullets = this.draft.bullets;
      if (this.send.images) {
        payload.imageRoles = this.imageRoles.map(i => ({
          url: i.url,
          role: i.role
        }));
      }
      return payload;
    },
    reviewContent() {
      if (this.previewing || !this.canReview) return;
      this.previewing = true;
      previewAmazonContent(this.contentPayload())
        .then(res => {
          this.contentPreview = res;
        })
        .catch(e => this.toast(this.apiError(e), "error"))
        .finally(() => {
          this.previewing = false;
        });
    },
    confirmPush() {
      if (this.pushing) return;
      this.pushing = true;
      pushAmazonContent(this.contentPayload())
        .then(res => {
          if (res.issues && res.issues.length) {
            this.toast(
              this.$t("message.marketplaces.editor_content_sent_issues", {
                count: res.issues.length
              }),
              "error"
            );
          } else {
            this.toast(this.$t("message.marketplaces.editor_content_sent"));
          }
          this.contentPreview = null;
          this.contentOpen = false;
          this.tab = "listing";
          this.$emit("updated");
          // Amazon procesa el PATCH de forma asíncrona: la ficha cacheada sigue
          // mostrando lo viejo hasta que el usuario vuelva a traerla.
          this.load();
        })
        .catch(e => this.toast(this.apiError(e), "error"))
        .finally(() => {
          this.pushing = false;
        });
    },
    fieldLabel(field) {
      const map = {
        title: "message.marketplaces.product_title",
        description: "message.marketplaces.editor_description",
        bullets: "message.marketplaces.editor_bullets",
        images: "message.marketplaces.publish_images"
      };
      return map[field] ? this.$t(map[field]) : field;
    },
    openUpload(replaceMain) {
      this.replaceMain = replaceMain;
      this.showFileSelector = true;
    },
    async fileUploaded(file) {
      this.showFileSelector = false;
      this.uploading = true;
      try {
        await saveImageToProduct(this.nkrCode, file.s3Url);
        await this.reloadImages();
        let detail = this.$t("message.marketplaces.editor_log_image_added");
        if (this.replaceMain) {
          // El alta no devuelve el id de forma fiable: la recién insertada es
          // la de id más alto, así que se resuelve sobre la lista ya recargada.
          const newest = this.dbImages.reduce(
            (max, img) => (!max || Number(img.id) > Number(max.id) ? img : max),
            null
          );
          if (newest) {
            await setMainImageApi(this.nkrCode, newest);
            await this.reloadImages();
            detail = this.$t("message.marketplaces.editor_log_main_replaced");
          }
        }
        await this.logAction(
          this.replaceMain ? "image_main_changed" : "image_uploaded",
          detail
        );
        this.toast(this.$t("message.marketplaces.editor_image_saved"));
      } catch (e) {
        this.toast(this.apiError(e), "error");
      } finally {
        this.uploading = false;
        this.replaceMain = false;
      }
    },
    async setMain(image) {
      if (this.settingMain) return;
      this.settingMain = true;
      try {
        await setMainImageApi(this.nkrCode, image);
        await this.reloadImages();
        await this.logAction(
          "image_main_changed",
          this.$t("message.marketplaces.editor_log_main_changed")
        );
        this.toast(this.$t("message.marketplaces.editor_main_set"));
      } catch (e) {
        this.toast(this.apiError(e), "error");
      } finally {
        this.settingMain = false;
      }
    },
    async useAiImage(image) {
      this.addingAiImageId = image.id;
      try {
        await saveImageToProduct(this.nkrCode, image.url);
        await this.reloadImages();
        await this.logAction(
          "image_uploaded",
          this.$t("message.marketplaces.editor_log_ai_image_added")
        );
        this.toast(this.$t("message.product_images.ai_image_added"));
      } catch (e) {
        this.toast(this.apiError(e), "error");
      } finally {
        this.addingAiImageId = null;
      }
    },
    // La bitácora vive en el store API pero la escritura de imágenes ocurre en
    // admin-main-api: se registra aparte para que el historial quede completo.
    // Si el registro falla, la acción ya se hizo: solo se loguea.
    async logAction(action, detail) {
      try {
        await logAmazonItemAction({
          nkrCode: this.nkrCode,
          sku: this.item ? this.item.sku : null,
          action,
          detail
        });
        await this.load();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("No se pudo registrar la acción", e);
      }
    }
  }
};
</script>

<style lang="postcss">
/* El editor compara dos columnas y abajo lleva formulario y bitácora: con el
   ancho por defecto todo queda en una columna angosta. */
#amazon-listing-editor .modal-content {
  width: 95%;
  max-width: 95%;
  max-height: 95vh;
}

/* El overlay reserva 100px arriba: sumados a un contenido de 95vh, el modal se
   sale de pantalla. En móvil el base ya lo pone a cero y eso se respeta. */
@media (min-width: 640px) {
  #amazon-listing-editor {
    padding-top: 2.5vh;
  }
}
</style>
